import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';
import type { Locale } from 'antd/es/locale';
import viVN from 'antd/locale/vi_VN';
import enGB from 'antd/locale/en_GB';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider, useTheme } from './components/ThemeProvider';
import type { Theme } from './components/ThemeProvider';
import { LanguageProvider, useNgonNgu } from './i18n';
import type { NgonNgu } from './i18n';
import { timMauNhan, timMucBoGoc } from './styles/tuy-chinh';
import { maLoi } from './lib/errors';
import { KHOA_NGU_CANH, lamMoiNguCanhCuaToi } from './modules/auth/hooks/useNguCanhCuaToi';
import { lamMoiNguCanhNenTang } from './modules/vai-tro-nen-tang/hooks/useNguCanhNenTang';
import { KHOA_VAI_TRO_NEN_TANG } from './modules/vai-tro-nen-tang/hooks/useVaiTroNenTang';

/**
 * Locale của antd theo ngôn ngữ đang chọn.
 *
 * Đây là thứ lo **chuỗi sẵn có của thư viện**: `Không có dữ liệu` của `Table`,
 * nhãn phân trang, nút của `DatePicker`, chữ trên `Upload`. Thiếu bước này thì
 * giao diện tiếng Anh vẫn còn những ô tiếng Việt mà không grep ra được, vì
 * chúng không nằm trong mã nguồn của kho.
 *
 * `en_GB` chứ không `en_US`: ngày là `dd/MM/yyyy` theo đặc tả.
 */
const ANTD_LOCALE: Record<NgonNgu, Locale> = {
  vi: viVN,
  en: enGB,
};

/**
 * Mã lỗi của Gateway chặn **mọi** API giữa phiên, không riêng màn đang mở:
 * - `TENANT_SUSPENDED`: tổ chức vừa bị tạm ngưng, kể cả với quản trị tổ chức (ADR-0077);
 * - `IP_BLOCKED` / `IP_NOT_ALLOWED`: IP vừa vào blocklist (ADR-0059 mục 6.1,
 *   ADR-0090 mục 3). Blocklist nền tảng chặn cả cổng nền tảng. Gateway không còn
 *   trả `IP_NOT_ALLOWED` từ ADR-0098 (dải tin cậy không chặn ai); giữ để an toàn.
 * - `CAN_XAC_THUC_TANG_CUONG`: vừa ra khỏi dải tin cậy mà tổ chức đòi OTP, phiên
 *   chưa qua OTP (ADR-0090 mục 2): mọi API đều trả mã này tới khi xác thực thêm.
 *
 * - `SAI_DIA_CHI_TRUY_CAP`: đang mở cổng qua domain thương hiệu không thuộc tổ chức của mình, hoặc domain vừa bị
 *   tạm ngưng (ADR-0097): mọi API qua địa chỉ này đều bị từ chối.
 */
const MA_CHAN_CA_CONG = new Set([
  'TENANT_SUSPENDED',
  'IP_BLOCKED',
  'IP_NOT_ALLOWED',
  'CAN_XAC_THUC_TANG_CUONG',
  'SAI_DIA_CHI_TRUY_CAP',
]);

function laNguCanh(queryKey: readonly unknown[]): boolean {
  const tenant = KHOA_NGU_CANH('').slice(0, 3);
  const nenTang = KHOA_VAI_TRO_NEN_TANG.nguCanh;
  return (
    tenant.every((phan, i) => queryKey[i] === phan) || nenTang.every((phan, i) => queryKey[i] === phan)
  );
}

/**
 * Bị chặn giữa phiên: tải lại ngữ cảnh của cổng (`me/context` hoặc
 * `platform-context`) để `NguCanhGate` thay cả cổng bằng màn chặn có câu chỉ
 * đường, thay vì mỗi màn hiện lỗi lẻ tẻ. Ngữ cảnh của cổng kia không được bật
 * nên làm mới nó không gọi gì. Bỏ qua lỗi của chính hai truy vấn ngữ cảnh để
 * không lặp vô hạn.
 */
function khiBiChan(err: unknown, queryKey?: readonly unknown[]): void {
  if (!MA_CHAN_CA_CONG.has(maLoi(err) ?? '')) return;
  if (queryKey && laNguCanh(queryKey)) return;
  lamMoiNguCanhCuaToi(queryClient);
  lamMoiNguCanhNenTang(queryClient);
}

/**
 * Giữ nguyên mặc định của TanStack Query theo bản gốc (§14.5): nhờ vậy hành vi
 * cache và vô hiệu hóa cache giống lúc chạy thật.
 */
const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (err, query) => khiBiChan(err, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (err) => khiBiChan(err) }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Bảng màu truyền cho antd theo từng chủ đề (§5.14). */
interface AntdPalette {
  surface: string;
  elevated: string;
  layout: string;
  subtle: string;
  border: string;
  borderSecondary: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  rowHover: string;
}

const PALETTE: Record<Theme, AntdPalette> = {
  light: {
    surface: '#FFFFFF',
    elevated: '#FFFFFF',
    layout: '#F8FAFC',
    subtle: '#F1F5F9',
    border: '#E2E8F0',
    borderSecondary: '#F1F5F9',
    text: '#0F172A',
    textSecondary: '#475569',
    textTertiary: '#586374',
    textDisabled: '#94A3B8',
    rowHover: '#F1F5F9',
  },
  dark: {
    surface: '#0F172A',
    elevated: '#1E293B',
    layout: '#020617',
    subtle: '#1E293B',
    border: '#334155',
    borderSecondary: '#243044',
    text: '#F8FAFC',
    textSecondary: '#CBD5E1',
    textTertiary: '#94A3B8',
    textDisabled: '#64748B',
    rowHover: '#1E293B',
  },
};

/** Giữ antd đồng bộ với chủ đề đang chọn. */
function AntdThemeBridge({ children }: { children: ReactNode }) {
  const { theme, tuyChinh } = useTheme();
  const { ngonNgu } = useNgonNgu();
  const isDark = theme === 'dark';
  const p = PALETTE[theme];
  const mauNhan = timMauNhan(tuyChinh.mauNhan).base;
  const boGoc = timMucBoGoc(tuyChinh.boGoc).px;

  const config = useMemo(
    () => ({
      cssVar: { key: 'fox' },
      hashed: false,
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: mauNhan,
        // Bán kính do người dùng chọn (ADR-UI-0007). antd tự suy các bậc phụ bằng
        // `genRadius()`, và `globals.css` đọc cùng giá trị qua `--user-radius`,
        // nên component của antd và class viết tay không bao giờ lệch góc nhau.
        borderRadius: boGoc,
        fontFamily: 'var(--font-body)',
        colorBgContainer: p.surface,
        colorBgElevated: p.elevated,
        colorBgLayout: p.layout,
        colorBorder: p.border,
        colorBorderSecondary: p.borderSecondary,
        colorText: p.text,
        colorTextSecondary: p.textSecondary,
        colorTextTertiary: p.textTertiary,
        // Không để antd tự suy `rgba(0,0,0,.25)` — màu đó nằm ngoài bảng màu.
        colorTextDisabled: p.textDisabled,
        colorFillAlter: p.subtle,
      },
      components: {
        Table: {
          // Nền đầu bảng là `subtle`, **không phải trắng** — đó là lý do bảng
          // trông có dải (§5.14).
          headerBg: p.subtle,
          headerColor: p.textSecondary,
          rowHoverBg: p.rowHover,
          borderColor: p.borderSecondary,
          headerSplitColor: 'transparent',
          colorBgContainer: p.surface,
        },
        Modal: { contentBg: p.surface, headerBg: p.surface },
        Segmented: { itemSelectedColor: p.text },
      },
    }),
    [isDark, p, mauNhan, boGoc],
  );

  return (
    <ConfigProvider locale={ANTD_LOCALE[ngonNgu]} theme={config}>
      {/* Vị trí và thời gian hiện toast cấu hình **một lần** ở đây (§5.14). */}
      <AntdApp notification={{ placement: 'bottomRight', duration: 3 }}>{children}</AntdApp>
    </ConfigProvider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <AntdThemeBridge>{children}</AntdThemeBridge>
        </QueryClientProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
